/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../core/components';
import { Component } from '@angular/core';
import {
    DownloadService,
    KeyStoreService,
    LogService,
    ModalService,
    PGPService,
    UserService
} from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { KeyStoreEnum } from '../../../../core/enums';
import { NamespaceService } from '../../shared/services/utils';
import { NamespacesApiService, OrganisationsApiService } from '../../shared/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { concatMap, takeUntil } from 'rxjs/operators';
import { nameValidation } from '../../../../core/directives';

@Component({
    selector: 'app-init-setup',
    templateUrl: './onboard.component.html',
    styleUrls: ['./onboard.component.scss', '../../../../pages/externalPageStyles.scss'],
})

export class OnboardComponent extends BaseComponent {
    /**
     * New organisation form group.
     */
    public organisationForm: FormGroup;

    /**
     * Store the current step.
     */
    public activeStep: number = 1;

    /**
     * Store the name of the organisation key.
     */
    public orgKeyName: string = 'lbOrgPrivate.key';

    constructor(
        private formBuilder: FormBuilder,
        private downloadService: DownloadService,
        private keyStoreService: KeyStoreService,
        private logService: LogService,
        private modalService: ModalService,
        private namespaceApiService: NamespacesApiService,
        private namespaceService: NamespaceService,
        private organisationApiService: OrganisationsApiService,
        private pgpService: PGPService,
        private router: Router,
        private userService: UserService
    ) {
        super();

        if (this.keyStoreService.storeState === KeyStoreEnum.CLOSED) {
            this.keyStoreService.storeState = KeyStoreEnum.OPEN;
        }

        this.setupForm();

        const organisationId = this.userService.getUser('organisationId');
        if (organisationId && organisationId > 0) {
            this.activeStep = 0;
        }
    }

    /**
     * Helper function to log user out.
     */
    public help(): void {
        const confirmationObject = {
            title: 'onboard_setup_issue_title',
            message: 'onboard_setup_issue_message',
            callBack: this.logOut.bind(this),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Move to the next step.
     */
    public nextStep(): void {
        this.activeStep++;
    }

    /**
     * Submit the organisation to the API.
     * @private
     */
    public submitOrganisation(): void {
        const formValues = this.organisationForm.value;
        const formData = { ...formValues.step1, ...formValues.step2 };
        if (!formData.contactNumber) {
            delete formData.contactNumber;
        }

        if (!formData.image) {
            delete formData.image;
        }
        this.organisationApiService.create(formData)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.updateApplication(data);
                    this.router.navigate(['cm/dashboard']).then(() => {
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AddOrganisationComponent: submitOrganisation', 'Error submitting data', error
                    );
                }
            });
    }

    /**
     * Patch step 1 data to form
     * @param formData
     */
    public patchOrganisationDataToForm(formData: { [key: string]: any; }): void {
        formData.contactNumber = formData.phoneNumber;
        this.organisationForm.get('step1').patchValue(formData);
        this.organisationForm.get('step1').markAsDirty();
    }

    /**
     * Take the users decrypted key and generate an organisation key and set it in the form.
     * @param decryptedUserPrivateKey
     */
    public generateOrgKeyUsingUserKey(decryptedUserPrivateKey: { armor: () => any; }): void {
        this.modalService.close('confirm-password'); // Close the modal after we know everything is correct.
        const passPhrase = decryptedUserPrivateKey.armor();
        const userData = {
            user: this.userService.getUser('email')
        };

        this.pgpService.generateNewKeyPair(userData, passPhrase)
            .pipe(
                concatMap(armoredKeys => this.decryptOrgPrivateKeyWithPassword(passPhrase, armoredKeys)),
                concatMap((
                        { armoredKeys, decryptedOrgPrivateKey }
                    ) => this.downloadDecryptedKey(armoredKeys, decryptedOrgPrivateKey)
                )
            )
            .subscribe({
                next: (armoredKeys: any): void => {
                    if (!this.keyStoreService.storeState) {
                        this.keyStoreService.storeState = KeyStoreEnum.OPEN;
                    }
                    this.keyStoreService.orgPrivateKey = armoredKeys.privateKey;
                    const form = this.organisationForm;
                    form.get('step2.publicKey').setValue(armoredKeys.publicKey);
                    form.get('step2.privateKey').setValue(armoredKeys.privateKey);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'OnboardComponent: generateOrgKeyUsingUserKey',
                        'Part of the generate org key function chain failed',
                        error
                    );
                }
            });
    }

    /**
     * Decrypt the newly created organisation key with the users decrypted private key as a passPhrase.
     * @param passPhrase
     * @param armoredKeys
     * @private
     */
    private decryptOrgPrivateKeyWithPassword(passPhrase: string, armoredKeys: {
        privateKey: string;
    }): Observable<any> {
        return new Observable<any>(observer => {
            this.pgpService.decryptPrivateKeyWithPassword(passPhrase, armoredKeys.privateKey, false)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (decryptedOrgPrivateKey: any): void => {
                        observer.next({ armoredKeys, decryptedOrgPrivateKey });
                        observer.complete();
                    },
                    error: (error: any): void => {
                        observer.error(error);
                    }
                });
        });

    }

    /**
     * Helper function to wrap the download service in an observable to complete the chain.
     * @param armoredKeys
     * @param decryptedOrgPrivateKey
     * @private
     */
    private downloadDecryptedKey(armoredKeys: any, decryptedOrgPrivateKey: { armor: () => any; }): Observable<any> {
        return new Observable<any>(observer => {
            this.downloadService.generateDownload(decryptedOrgPrivateKey.armor(), this.orgKeyName, { type: 'type/plain' });
            observer.next(armoredKeys);
            observer.complete();
        });
    }

    /**
     * Set up the new load balancer form.
     * @private
     */
    private setupForm(): void {
        this.organisationForm = this.formBuilder.group({
            step1: this.formBuilder.group({
                image: [null],
                name: [null, [Validators.required, Validators.minLength(3), nameValidation]],
                countryCode: [null],
                contactNumber: [null]
            }),
            step2: this.formBuilder.group({
                publicKey: [null, Validators.required],
                privateKey: [null, Validators.required]
            })
        });
    }

    /**
     * Update the application with new user organisation ID and set namespaces.
     * @param data
     * @private
     */
    private updateApplication(data: { token: any; id: any; }): void {
        const newJWT = data.token;
        if (newJWT) {
            this.userService.setUserValueByProperty('JWT', newJWT);
        }

        const organisationId = data.id;
        if (organisationId) {
            this.userService.setUserValueByProperty('organisationId', organisationId);
            this.updateNamespaceWithDefault(organisationId);
        }

        this.userService.setUserValueByProperty('permissions', 2);
    }

    /**
     * Update the name space service and dropdown.
     * @param organisationId
     * @private
     */
    private updateNamespaceWithDefault(organisationId: string): void {
        this.namespaceApiService.getAll(organisationId)
            // .pipe(takeUntil(this.destroyed$)) TODO doesn't work with pipe in place!
            .subscribe({
                next: (namespaces: any[]): void => {
                    this.namespaceService.namespaces = namespaces;
                },
                error: (error: any): void => {
                    this.logService.error('AddOrganisationComponent: updateNamespaceWithDefault', error);
                }
            });
    }

    /**
     * Call back function to close modal and log the user out.
     * @private
     */
    private logOut(): void {
        this.modalService.close('confirmation-box');
        this.userService.logout();
    }
}
